/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useAppDispatch} from 'helpers/store'
import {BookingReducerState as ReservationReducerState} from 'store/booking/BookingReducer'
import {PersonType} from 'types/enums/PersonType'
import {PackageItemPersonType} from 'types/enums/PackageItemPersonType'
import {Reservation} from 'types/entities/Reservation'
import {ReservationSlot} from 'types/entities/ReservationSlot'
import {Ticket} from 'types/entities/Ticket'
import BookingAction from '../store/booking/BookingAction'
import {useTranslation} from 'react-i18next'
import Dates from 'utils/Dates'
import BookingLoginAction from 'store/bookingLogin/BookingLoginAction'
import {useTypedSelector} from 'helpers/reducers'
import {TimeslotViewMode} from 'types/enums/TimeslotViewMode'
import CalendarAction from 'store/calendar/CalendarAction'
import type {RefObject} from 'react'
import {useMemo} from 'react'
import {ReservationStatus, ReservationVisibilityType,} from 'types/enums/ReservationStatus'
import {closeModal, openModal} from 'helpers/actions'
import {ModalSize} from 'types/enums/ModalSize'
import {ReservationOfferStatus} from 'types/enums/ReservationOfferStatus'
import {PackageCategory} from "types/enums/PackageCategory";

export interface ReservationOfferStatusDescriptor {
    value: ReservationOfferStatus
    label: string
}

export interface ReservationStatusDescriptor {
    value: ReservationStatus | 'all'
    label: string
}

export const useReservationOfferStatusOptions = () => {
    const { t } = useTranslation()

    const options = useMemo<Array<{ label: string; value: ReservationOfferStatus }>>(
        () => [
            { label: t('All'), value: ReservationOfferStatus.All },
            { label: t('Lead'), value: ReservationOfferStatus.Lead },
            { label: t('New'), value: ReservationOfferStatus.New },
            { label: t('Sent'), value: ReservationOfferStatus.Sent },
            { label: t('Accepted'), value: ReservationOfferStatus.Accepted },
            { label: t('Finished'), value: ReservationOfferStatus.Finished },
            { label: t('Declined'), value: ReservationOfferStatus.Declined },
            { label: t('Reconfirm'), value: ReservationOfferStatus.Reconfirm },
        ],
        [t]
    )

    const map = useMemo(() => {
        const statusMap = {} as Record<ReservationOfferStatus, ReservationOfferStatusDescriptor>
        options.forEach((option) => {
            statusMap[option.value] = option
        })
        return statusMap
    }, [options])

    const getStatusDescriptor = (value: ReservationOfferStatus) => map[value] ?? map[ReservationOfferStatus.All]

    return { options, map, getStatusDescriptor }
}

export const useReservationStatusOptions = (isOnlineOnly: boolean) => {
    const { t } = useTranslation()

    const options = useMemo<Array<{ label: string; value: ReservationStatus | 'all' }>>(() => {
        return isOnlineOnly
            ? [{ label: t('Reserved'), value: ReservationStatus.Reserved }]
            : [
                  { label: t('All'), value: ReservationStatus.All },
                  // { label: t('Creating'), value: ReservationStatus.Creating },
                  // { label: t('Pending'), value: ReservationStatus.Pending },
                  { label: t('Confirmed'), value: ReservationStatus.Confirmed },
                  // { label: t('Reserved'), value: ReservationStatus.Reserved },
                  { label: t('Finished'), value: ReservationStatus.Finished },
                  { label: t('Cancelled'), value: ReservationStatus.Cancelled },
              ]
    }, [isOnlineOnly, t])

    const map = useMemo(() => {
        const statusMap = {} as Record<string, ReservationStatusDescriptor>
        options.forEach((option) => {
            statusMap[option.value] = option
        })
        return statusMap
    }, [options])

    const getStatusDescriptor = (value: ReservationStatus | 'all') => map[value] ?? map[ReservationStatus.All]

    return { options, map, getStatusDescriptor }
}

export interface ReservationTicketsState {
    adultRacesLeft: number
    childRacesLeft: number
    totalRacesLeft: number
}

interface ReservationPersons {
    personType: PackageItemPersonType
    persons_count: number
}

/**
 * FlatMap of ReservationPersons
 *
 * personType: PackageItemPersonType
 * persons_count: number
 * @returns ReservationPersons[], array of ReservationPersons
 */
const reservationPersonTypes = (reservations: Reservation[]): ReservationPersons[] => {
    return reservations
        .filter((reservation: Reservation) => {
            if (!reservation.reservationSlots) {
                return false
            }
            return reservation.reservationSlots.length > 0
        })
        .map((reservation: Reservation) => {
            return reservation.reservationSlots.map((reservationSlot: ReservationSlot) => {
                const personType: PackageItemPersonType =
                    reservationSlot.packageItem?.person_type || PackageItemPersonType.Family
                const reservationPersonsType: ReservationPersons = {
                    personType: personType,
                    persons_count: reservationSlot.persons_count,
                }
                return reservationPersonsType
            })
        })
        .flatMap((obj) => obj)
}

/**
 * Reduce array of ReservationPersons by personTypeValue
 * @param arr
 * @param val
 * @returns number, count of persons
 */
const countPersonsType = (arr: ReservationPersons[], val: PackageItemPersonType) =>
    arr.reduce((a: number, v: ReservationPersons): number => {
        return v.personType == val ? a + v.persons_count : a
    }, 0)

/**
 * Return a negative value of needed tickets for all outstanding reservations
 * works correctly only if a state has only current reservations
 * @param reservations
 */
export const hasEnoughForAll = (reservations: Reservation[]): ReservationTicketsState => {
    if (!reservations) {
        return {
            adultRacesLeft: 0,
            childRacesLeft: 0,
            totalRacesLeft: 0,
        }
    }

    const adultRacesLeft = -1 * countPersonsType(reservationPersonTypes(reservations), PackageItemPersonType.Adult)
    const childRacesLeft = -1 * countPersonsType(reservationPersonTypes(reservations), PackageItemPersonType.Child)
    const hasEnoughTicketsForAll = {
        adultRacesLeft: adultRacesLeft,
        childRacesLeft: childRacesLeft,
        totalRacesLeft: adultRacesLeft + childRacesLeft,
    }
    return hasEnoughTicketsForAll
}

/**
 * Return if profile has enough Tickets for current reservation
 *
 */
export const hasEnoughTickets = (
    tickets: Ticket[],
    reservation: ReservationReducerState,
    weekDay?: number | null
): ReservationTicketsState => {
    const shiftDate = weekDay ? (+weekDay === 0 ? 7 : weekDay) : null //fix php to js sundays

    const adultRacesLeft = getTicketRacesLeftInReservation(tickets, reservation, PersonType.Adult, shiftDate)
    const childRacesLeft = getTicketRacesLeftInReservation(tickets, reservation, PersonType.Child, shiftDate)

    const hasEnough: ReservationTicketsState = {
        adultRacesLeft: adultRacesLeft,
        childRacesLeft: childRacesLeft,
        totalRacesLeft: adultRacesLeft + childRacesLeft,
    }
    return hasEnough
}

/**
 * Return if profile has enough Tickets for current reservation
 *
 */
export const hasEnoughTicketsWithoutReservation = (
    tickets: Ticket[],
    weekDay?: number | null
): ReservationTicketsState => {
    const shiftDate = weekDay ? (+weekDay === 0 ? 7 : weekDay) : null //fix php to js sundays

    const adultRacesLeft = ticketsLeftOnProfile(tickets, PersonType.Adult, weekDay)
    const childRacesLeft = ticketsLeftOnProfile(tickets, PersonType.Child, shiftDate)

    const hasEnough: ReservationTicketsState = {
        adultRacesLeft: adultRacesLeft,
        childRacesLeft: childRacesLeft,
        totalRacesLeft: adultRacesLeft + childRacesLeft,
    }
    return hasEnough
}

/**
 * General function that returns sum of objects by their matching keys
 * @param objs
 */
export const sumObjectsByKey = (...objs: any[]) => {
    return objs.reduce((a, b) => {
        for (const k in b) {
            if (Object.prototype.hasOwnProperty.call(b, k)) a[k] = (a[k] || 0) + b[k]
        }
        return a
    }, {})
}

/** Check for tickets available on profile, if there are no tickets, return 0
 *
 * @param tickets
 * @param personType
 * @param weekDay
 */
export const ticketsLeftOnProfile = (tickets: Ticket[], personType?: PersonType, weekDay?: number | null): number => {
    if (!tickets) {
        console.error('No Tickets found in profile')
        return 0
    }
    return tickets.reduce((agg: number, cur: Ticket) => {
        if (cur.person_type === personType) {
            //if this is found, ticket is not counted in. Should be done by Timeslots (on date change, weekDay => apply only on ), Footer next button (on filter, refresh)

            const isTicketBlacklisted = cur.article?.articleBlacklist && cur.article?.articleBlacklist.length > 0
            const ticketBlacklistedIndex = cur.article?.articleBlacklist?.findIndex((articleListBlacklist) => {
                return articleListBlacklist.day === weekDay
            })
            if (!isTicketBlacklisted) {
                return agg + cur.race_count_left
            }

            if (typeof weekDay !== 'undefined') {
                // count in only tickets, that have blacklist set and are not blacklisted
                if (typeof ticketBlacklistedIndex !== 'undefined' && ticketBlacklistedIndex != -1) {
                    //if the blacklisted is found in index, dont count ticket
                    return agg
                }
                if (isTicketBlacklisted && ticketBlacklistedIndex === -1) {
                    return agg + cur.race_count_left
                }
            } else {
                // count in only tickets, that have blacklist set and are not blacklisted

                if (isTicketBlacklisted && ticketBlacklistedIndex === -1) {
                    return agg
                }
            }
            // person type is matched and nothing else (no week day limit on this ticket, e.g. normal ticket
            return agg + cur.race_count_left
        }
        //reduce all other unmatched tickets anyway
        return agg
    }, 0)
}

export const getTicketRacesLeftInReservation = (
    tickets: Ticket[],
    reservation: ReservationReducerState,
    personType: PersonType,
    weekDay?: number | null
): number => {
    // returns tickets left for reservation, but should count in all tickets for that day. This function overwrites per person Type based on the ticket, that is still available, but already has null tickets
    const ticketsLeft = (pt: PersonType) => ticketsLeftOnProfile(tickets, pt, weekDay)
    //reservation needs ticket on weekDay, but if this number is negative, we dont have any given tickets on this weekday
    const reservationTicketsNeeded = (reservation as any).drivers
        .filter((driver: any) => driver.person_type === personType)
        .map((driver: any) => driver.races_count)
        .reduce((a: number, b: number) => a + b, 0)
    return ticketsLeft(personType) - reservationTicketsNeeded
}

export const hasEnoughTicketForProfile = (
    tickets: Ticket[],
    profile: any,
    reservation: ReservationReducerState,
    weekDay: number | null
): boolean => {
    const hasEnough = hasEnoughTickets(tickets, reservation, weekDay)
    switch (profile.person_type) {
        case PersonType.Adult:
            return hasEnough.adultRacesLeft >= enoughTickets.profile
        case PersonType.Child:
            return hasEnough.childRacesLeft >= enoughTickets.profile
        default:
            return hasEnough.totalRacesLeft >= enoughTickets.profile
    }
}
export const uniqueDriversInReservation = (data: any[]) => {
    return data.reduce((acc, obj) => {
        const exist = acc.find(({ id }: any) => obj.id === id)
        if (!exist) {
            acc.push(obj)
        }
        return acc
    }, [])
}
export const enoughTickets = { notEnough: -1, profile: 0, newRacer: 1 }

export const useReservationStatus = (package_cart_guid: string, package_cart_token: string) => {
    const dispatch = useAppDispatch()
    const getDrivers = () => {
        return (
            dispatch(BookingAction.getReservationDrivers(package_cart_guid, package_cart_token) as any) as any
        ).then((r: any) => {
            return r
        })
    }

    const getStatus = () => {
        return (dispatch(BookingAction.getReservationStatus(package_cart_guid, package_cart_token) as any) as any).then(
            (r: any) => {
                return r
            }
        )
    }

    return {
        getStatus,
        getDrivers,
    }
}

export const subtractMinutesFromTime = (hour: number, minute: number, subtractMinutes: number) => {
    // Create a new Date object and set the hours and minutes
    const dateObj = new Date()
    dateObj.setHours(hour)
    dateObj.setMinutes(minute)

    // Subtract 15 minutes
    dateObj.setMinutes(dateObj.getMinutes() - subtractMinutes)

    // Get the updated hours and minutes
    const updatedHour = dateObj.getHours()
    const updatedMinute = dateObj.getMinutes()

    // Format the result as "hour:minute"
    const result = `${updatedHour.toString().padStart(2, '0')}:${updatedMinute.toString().padStart(2, '0')}`

    return result
}

export const useReservationStatusForLogin = () => {
    const { t } = useTranslation()
    const midnight = new Date()
    midnight.setHours(0, 0, 0, 0)

    const getStatus = (reservation: Reservation) => {
        const reservationDateTime = new Date(reservation.date).getTime()
        const canEdit = reservationDateTime > midnight.getTime()
        const isReservationPrepared = reservation.persons === reservation?.reservationDrivers?.length

        if (canEdit) {
            if (isReservationPrepared) {
                return {
                    value: true,
                    label: t('Prepared'),
                    name: 'prepared',
                }
            } else {
                return {
                    value: true,
                    label: t('Quick Check-in'),
                    name: 'prepare',
                }
            }
        } else {
            return {
                value: false,
                label: t('Completed'),
                name: 'completed',
            }
        }
    }

    return {
        getStatus,
    }
}

export const sortReservationsByDate = (reservations: Reservation[]) => {
    return [...reservations].sort((reservationA, reservationB) => {
        const reservationATime = new Date(reservationA.date).getTime()
        const reservationBTime = new Date(reservationB.date).getTime()
        return reservationBTime - reservationATime
    })
}

export const getShortNameFromId = (id_string: string | undefined | PackageCategory) => {
    if (!id_string) {
        return 'Undefined'
    }

    switch (id_string) {
        case PackageCategory.EventS:
            return 'Fun'
        case PackageCategory.EventL:
            return 'Ultra'
        case PackageCategory.EventM:
            return 'Mega'
        case PackageCategory.General:
            return 'Standard'
        case PackageCategory.Main:
            return 'Standard'
        case PackageCategory.Action:
            return 'Action / Family'
        case PackageCategory.School:
            return 'School'
        case PackageCategory.SchoolTrophy:
            return 'School'
        case PackageCategory.MegaFunSchool:
            return 'Mega Fun School'
        case PackageCategory.FerienPass:
            return 'Ferien Pass'
        case PackageCategory.Kinder:
            return 'Kinder'
        case PackageCategory.FerienPassB:
            return 'Ferien Pass Easy'
        case PackageCategory.EventMegaFun:
            return 'Mega Fun'
        case PackageCategory.EventNonPlusUltra:
            return 'Non Plus Ultra'
        case PackageCategory.PolterabendMegaFun:
            return 'Polterabend Mega Fun'
        case PackageCategory.PolterabendNonPlusUltra:
            return 'Polterabend Non Plus Ultra'
        default:
            return 'Undefined'
    }
}

export interface ReservationPlanTimepickerHoursAndMinutes {
    minuteStep: number
    disabledHours: () => number[]
    defaultHour: number
}

export const calculateReservationPlanTimepickerHoursAndMinutes = (
    reservationStartTime: string,
    startHourBefore: number,
    minuteIntervals: number
): ReservationPlanTimepickerHoursAndMinutes => {
    const firstHour = Dates.hour(reservationStartTime) - startHourBefore
    const disabledHours = () => Array.from({ length: firstHour }, (_, i) => i)
    const defaultHour = firstHour

    return {
        minuteStep: minuteIntervals,
        disabledHours: disabledHours,
        defaultHour: defaultHour,
    }
}

export const useAssignCurrentUserToTicketHandler = () => {
    const dispatch = useAppDispatch()
    const fetchReservationAssignDriver = async (
        driver_id: number,
        packages_cart_item_id: number,
        reservation_id: number
    ) => {
        try {
            const r = await dispatch(
                BookingLoginAction.fetchReservationAssignDriver({
                    reservation_id,
                    packages_cart_item_id,
                    driver_id,
                })
            )
            if (r.reservationDriver) {
                dispatch(BookingLoginAction.setReservationDriverToSelectedReservation(r.reservationDriver))
            }
            return r
        } catch (e) {
            console.error(e)
            return undefined
        }
    }

    return {
        fetchReservationAssignDriver,
    }
}

export const useReservationViewModes = () => {
    const timeslotViewMode = useTypedSelector((state) => state.calendar.timeslotViewMode)
    const dispatch = useAppDispatch()
    const viewModes = [TimeslotViewMode.Reservation, TimeslotViewMode.Editing, TimeslotViewMode.Blocking]
    const viewModeNextIndex = viewModes.indexOf(timeslotViewMode) + 1

    const rotateViewModes = () => {
        dispatch(CalendarAction.resetTimeslotReservationSelect())
        dispatch(
            CalendarAction.setTimeslotViewMode(
                viewModeNextIndex >= viewModes.length ? viewModes[0] : viewModes[viewModeNextIndex]
            )
        )
    }
    return {
        rotateViewModes,
    }
}

export const useScrollIntoView = (timeRef: RefObject<HTMLDivElement | null>) => {
    const scroll = () => {
        if (!timeRef || !timeRef.current) {
            return
        }
        timeRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
        })
    }

    return {
        scroll,
    }
}

export const filterReservationsVisibility = (
    reservations: Reservation[],
    visibilityType: ReservationVisibilityType
) => {
    switch (visibilityType) {
        case ReservationVisibilityType.All:
            return reservations
        case ReservationVisibilityType.Valid:
            return reservations.filter((res) =>
                [
                    ReservationStatus.Reserved,
                    ReservationStatus.Finished,
                    ReservationStatus.Pending,
                    ReservationStatus.Rebooking,
                    ReservationStatus.Confirmed,
                    ReservationStatus.NoShow,
                ].includes(res.status as ReservationStatus)
            )
        case ReservationVisibilityType.Invalid:
            return reservations.filter(
                (res) =>
                    ![
                        ReservationStatus.Reserved,
                        ReservationStatus.Finished,
                        ReservationStatus.Pending,
                        ReservationStatus.Rebooking,
                        ReservationStatus.Confirmed,
                        ReservationStatus.NoShow,
                    ].includes(res.status as ReservationStatus)
            )
        case ReservationVisibilityType.New:
            return reservations.filter((res) => [ReservationStatus.Creating].includes(res.status as ReservationStatus))
    }
}

export interface ReservationOverlappingProps {
    reservation: Reservation
    reservationIndex: number
    offsetIndex: number
}

export const filterReservationSortedWithVisibility = (
    reservations: Reservation[],
    reservationVisibilityType: ReservationVisibilityType
): Reservation[] => {
    if (!reservations) {
        return []
    }
    return filterReservationsVisibility(reservations, reservationVisibilityType).sort(
        (a: Reservation, b: Reservation) => {
            if (!a.date_time || !b.date_time) {
                return -1
            }
            if (new Date(a.date_time) == new Date(b.date_time)) {
                return new Date(a.created_at) > new Date(b.created_at) ? 1 : -1
            }
            return new Date(a.date_time) > new Date(b.date_time) ? 1 : -1
        }
    )
}

export const useReservationOverlapping = (reservations: Reservation[]) => {
    const allCheckedReservations: ReservationOverlappingProps[] = []

    const highestOffsetIndex = (checkedReservations: ReservationOverlappingProps[]) => {
        let highestOffsetIndex = 0
        checkedReservations.map((reservationOverlapping) => {
            if (reservationOverlapping.offsetIndex > highestOffsetIndex) {
                highestOffsetIndex = reservationOverlapping.offsetIndex
            }
        })
        return highestOffsetIndex
    }

    const findClosestReservationByOffsetIndex = (reservationIndex: number, offsetIndex: number) => {
        const reversedAllCheckedReservations = [...allCheckedReservations]
        return reversedAllCheckedReservations
            .reverse()
            .find(
                (reservation) =>
                    reservation.reservationIndex < reservationIndex && reservation.offsetIndex === offsetIndex
            )
    }

    const checkReservationsOverlapping = (a: Reservation, b: Reservation) => {
        if (!a.end_date_time || !b.end_date_time || !a.date_time || !b.date_time) {
            return
        }
        return !(a.end_date_time <= b.date_time || a.date_time >= b.end_date_time)
    }

    const findOffsetIndex = (selectedReservation: Reservation, reservationIndex: number, offsetIndex: number) => {
        const closesReservationIndex = findClosestReservationByOffsetIndex(reservationIndex, offsetIndex)

        if (!closesReservationIndex) {
            return offsetIndex
        }

        const isOverlappedReservation = checkReservationsOverlapping(
            selectedReservation,
            closesReservationIndex.reservation
        )
        if (isOverlappedReservation) {
            return findOffsetIndex(selectedReservation, reservationIndex, offsetIndex + 1)
        } else {
            return offsetIndex
        }
    }

    /**
     * najdi reservationIndex v reservations podla reservation id,
     * najdi najblizsiu reservation v allCheckedReservations s nizsim reservationIndex a s offsetIndex 0,
     * skontroluj ci nastava overlapping,
     * ak NENASTAVA overlapping vrat offsetIndex,
     * ak nastava znova hladaj v allCheckedReservations s offsetIndex x+1,
     * znova skontroluj ci nastava overlapping (loop)
     * ak nevrati reservation, vrat offsetIndex
     *
     * @param selectedReservation
     */
    const calculateOffsetIndexFromReservation = (selectedReservation: Reservation) => {
        const reservationIndex = reservations.findIndex((reservation) => reservation.id === selectedReservation.id)
        const calculatedReservation: ReservationOverlappingProps = {
            reservationIndex: reservationIndex,
            reservation: selectedReservation,
            offsetIndex: findOffsetIndex(selectedReservation, reservationIndex, 0),
        }
        allCheckedReservations.push(calculatedReservation)
        return calculatedReservation
    }

    /**
     * Zisti ci je v calendar week view sam a moze byt fullwidth zobrazenie rezervacie
     *
     * @param reservationOverlapping
     * @param allReservationsOverlapping
     */
    const isFullWidthReservation = (
        reservationOverlapping: ReservationOverlappingProps,
        allReservationsOverlapping: ReservationOverlappingProps[]
    ) => {
        if (reservationOverlapping.offsetIndex === 0) {
            const nextReservation = allReservationsOverlapping.find(
                (res) => res.reservationIndex === reservationOverlapping.reservationIndex + 1
            )
            if (nextReservation) {
                return nextReservation.offsetIndex === 0
            }
            return true
        }
        return false
    }

    const init = reservations.map((reservation, reservationIndex): ReservationOverlappingProps => {
        const calculatedReservation = calculateOffsetIndexFromReservation(reservation)
        return {
            reservationIndex: reservationIndex,
            reservation: reservation,
            offsetIndex: calculatedReservation.offsetIndex,
        }
    })

    return {
        isFullWidthReservation,
        highestOffsetIndex,
        init,
    }
}

export const useReservationModalHandler = (
    reservation: Reservation,
    openingMode: 'singleClick' | 'doubleClick',
    callBack: () => void
) => {
    const dispatch = useAppDispatch()
    const reservation_id: number = reservation.id

    const openReservation = (e: React.MouseEvent<HTMLButtonElement>, component: React.ComponentType<any>) => {
        let timeslots_ids: number[] = []
        if (typeof reservation.timeslots !== 'undefined') {
            timeslots_ids = reservation.timeslots.map((timeslot) => timeslot.id)
        } else {
            timeslots_ids = reservation.reservationSlots.map((resSlot) => resSlot.timeslot_id)
        }

        const openReservationModal = () => {
            dispatch(CalendarAction.fetchReservation(reservation_id)).then(() => {
                return dispatch(
                    openModal(component as any, {
                        modalSize: ModalSize.RoundedFullscreen,
                        closeModal: () => {
                            callBack()
                            dispatch(closeModal())
                        },
                    })
                )
            })
        }
        if (openingMode === 'singleClick') {
            return openReservationModal()
        }
        switch (e.detail) {
            case 1:
                return dispatch(CalendarAction.setSelectedReservationTimeslots(timeslots_ids))
            case 2:
                return openReservationModal()
            default:
                return
        }
    }
    return {
        openReservation,
    }
}
